import React from 'react';
import { Play, ChevronDown } from 'lucide-react';
import { siteConfig, hostInfo, podcastPlatforms } from '../data/mock';
import { Button } from './ui/button';
import { useTheme } from '../context/ThemeContext';

const HeroSection = () => {
  const { isDark } = useTheme();
  
  const scrollToEpisodes = () => {
    const element = document.querySelector('#episodes');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="home"
      className={`relative min-h-screen flex items-center pt-20 overflow-hidden ${isDark ? 'bg-stone-950' : 'bg-stone-50'}`}
    >
      {/* Background with grain texture */}
      <div className={`absolute inset-0 ${isDark ? 'bg-stone-950' : 'bg-stone-50'}`}>
        <div className={`absolute inset-0 ${isDark ? 'bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-amber-900/20 via-stone-950 to-stone-950' : 'bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-amber-100/60 via-stone-50 to-stone-50'}`} />
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
        }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            {/* Badge */}
            <button 
              onClick={scrollToEpisodes}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full cursor-pointer transition-all hover:scale-105 ${isDark ? 'bg-amber-500/10 border border-amber-500/30 hover:bg-amber-500/20' : 'bg-amber-500/20 border border-amber-500/40 hover:bg-amber-500/30'}`}
            >
              <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
              <span className="text-amber-600 text-sm font-medium">New Episodes Weekly</span>
            </button>

            {/* Main Heading */}
            <div className="space-y-4">
              <h1 className={`text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>
                {siteConfig.name}
              </h1>
              <p className={`text-2xl sm:text-3xl font-light ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                {siteConfig.tagline}
              </p>
            </div>

            {/* Description */}
            <p className={`text-lg leading-relaxed max-w-xl ${isDark ? 'text-stone-400' : 'text-stone-600'}`}>
              {siteConfig.description}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <Button
                onClick={scrollToEpisodes}
                className="bg-amber-500 hover:bg-amber-600 text-stone-950 font-semibold px-8 py-6 text-lg group"
              >
                <Play className="mr-2 h-5 w-5 group-hover:scale-110 transition-transform" />
                Listen Now
              </Button>
              <Button
                variant="outline"
                onClick={() => document.querySelector('#about').scrollIntoView({ behavior: 'smooth' })}
                className={`px-8 py-6 text-lg ${isDark ? 'border-stone-600 text-stone-300 hover:bg-stone-800 hover:text-stone-100' : 'border-stone-300 text-stone-600 hover:bg-stone-100 hover:text-stone-800'}`}
              >
                Learn More
              </Button>
            </div>

            {/* Platform Links */}
            <div className="pt-4">
              <p className={`text-sm mb-3 ${isDark ? 'text-stone-500' : 'text-stone-500'}`}>Available on</p>
              <div className="flex gap-4">
                {podcastPlatforms.map((platform) => (
                  <a
                    key={platform.name}
                    href={platform.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`px-4 py-2 border rounded-lg transition-all text-sm font-medium ${
                      isDark 
                        ? 'bg-stone-900 hover:bg-stone-800 border-stone-800 text-stone-400 hover:text-amber-400' 
                        : 'bg-white hover:bg-stone-50 border-stone-200 text-stone-600 hover:text-amber-600 shadow-sm'
                    }`}
                  >
                    {platform.name}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Right Content - Host Image */}
          <div className="relative">
            <div className="relative z-10">
              {/* Decorative border */}
              <div className={`absolute -inset-4 rounded-3xl blur-2xl ${isDark ? 'bg-gradient-to-br from-amber-500/20 to-orange-600/20' : 'bg-gradient-to-br from-amber-300/40 to-orange-400/30'}`} />
              
              {/* Main image container */}
              <div className={`relative p-2 rounded-3xl ${isDark ? 'bg-gradient-to-br from-amber-500/10 to-transparent' : 'bg-gradient-to-br from-amber-200/30 to-transparent'}`}>
                <img
                  src={hostInfo.image}
                  alt={hostInfo.name}
                  className="w-full h-auto rounded-2xl object-cover shadow-2xl"
                />
                
                {/* Floating card */}
                <div className={`absolute -bottom-6 -left-6 backdrop-blur-sm border rounded-2xl p-4 shadow-xl ${isDark ? 'bg-stone-900/90 border-stone-800' : 'bg-white/90 border-stone-200'}`}>
                  <p className={`font-semibold ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>Hosted by</p>
                  <p className={`text-lg font-bold ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>{hostInfo.name}</p>
                  <p className={`text-sm ${isDark ? 'text-stone-500' : 'text-stone-500'}`}>{hostInfo.title}</p>
                </div>
              </div>
            </div>

            {/* Decorative elements */}
            <div className={`absolute top-1/4 -right-8 w-24 h-24 rounded-full blur-2xl ${isDark ? 'bg-amber-500/10' : 'bg-amber-300/30'}`} />
            <div className={`absolute bottom-1/4 -left-8 w-32 h-32 rounded-full blur-2xl ${isDark ? 'bg-orange-600/10' : 'bg-orange-300/30'}`} />
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className={`w-8 h-8 ${isDark ? 'text-stone-600' : 'text-stone-400'}`} />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
